/**
 * PromptHelper - 画面描述写作技巧提示组件
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, X, CheckCircle, AlertCircle } from 'lucide-react';

const EXAMPLES = [
  {
    title: '示例 1：具体场景',
    good: '一只橙色的小狐狸站在大树下，抬头望着树冠上的金色阳光，周围是绿色草地和白色小花，画面温暖明亮',
    why: '包含了场景、角色、动作、颜色、氛围',
  },
  {
    title: '示例 2：有氛围感',
    good: '夜晚的森林，月光透过树叶洒下银色光斑，小狐狸蜷缩在树洞里，表情有点害怕但勇敢，画面偏冷色调',
    why: '时间、光线、角色情绪、色调都很清晰',
  },
  {
    title: '示例 3：互动场景',
    good: '小狐狸和小鸟在溪边玩耍，溪水清澈见底，周围是五颜六色的野花，阳光明媚，两个角色都在笑',
    why: '多角色、互动动作、环境细节、情绪表达',
  },
];

const ANIMAL_EXAMPLES = [
  {
    title: '单动物场景',
    good: '一只白色的小兔子站在蘑菇屋门口，长耳朵竖起来，手里抱着一颗红色的胡萝卜，周围是绿色草地和彩色小花，阳光明媚',
    why: '物种特征、标志性动作、环境细节都很具体',
  },
  {
    title: '多动物互动',
    good: '小兔子和小狐狸并肩坐在大树下，小兔子指着天上的彩虹，小狐狸抬头看，表情惊喜，两只动物都笑得很开心，背景是雨后的森林，空气中有小水珠',
    why: '多角色的动作、表情、互动关系都描述清楚了',
  },
  {
    title: '动物动作场景',
    good: '小兔子蹦蹦跳跳地穿过小溪，溪水溅起小水花，小鸟在它头顶飞着带路，周围是茂密的树木和远处的山，画面充满活力',
    why: '动作描写生动，有动态感，环境有层次',
  },
];

const TIPS = [
  { icon: '🏞️', text: '先写场景在哪里（森林、太空、家里、海边...）' },
  { icon: '🏃', text: '再写角色在做什么动作（跑、跳、思考、拥抱...）' },
  { icon: '🌈', text: '加上氛围和颜色（阳光/雨天、温暖/紧张、绿色/金色...）' },
  { icon: '👀', text: '尽量具体，少用"漂亮的""好看的"这类模糊词' },
];

const ANIMAL_TIPS = [
  { icon: '🐰', text: '先写物种特征：毛色、体型、标志性部位（长耳朵、尖尾巴）' },
  { icon: '🏃', text: '再写动作：跳、跑、飞、坐、抱、指' },
  { icon: '😊', text: '加上表情：开心、惊讶、害怕、勇敢、好奇' },
  { icon: '🌲', text: '环境衬托：森林、草地、洞穴、小溪、天空' },
];

const WARNINGS = [
  '❌ "画一只狐狸" —— 太简单，AI 会自由发挥',
  '❌ "像梵高风格" —— 画风在 Step3 已选，这里只描述画面内容',
  '❌ "小狐狸很开心地去冒险" —— 情绪词太多，缺少画面细节',
];

export default function PromptHelper() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-full transition-colors"
      >
        <Lightbulb className="w-3.5 h-3.5" />
        画面描述技巧
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* 遮罩 */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* 抽屉 */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 overflow-y-auto"
            >
              <div className="p-6">
                {/* 头部 */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-500" />
                    画面描述写作技巧
                  </h2>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                  >
                    <X className="w-4 h-4 text-slate-500" />
                  </button>
                </div>

                {/* 写作步骤 */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">📝 四步写出好描述</h3>
                  <div className="space-y-2">
                    {TIPS.map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-2 bg-indigo-50 rounded-lg p-3">
                        <span className="text-lg shrink-0">{tip.icon}</span>
                        <p className="text-sm text-slate-700">{tip.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 示例 */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">✅ 好的示例</h3>
                  <div className="space-y-3">
                    {EXAMPLES.map((ex, idx) => (
                      <div key={idx} className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                        <div className="flex items-center gap-1.5 mb-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className="text-xs font-semibold text-emerald-700">{ex.title}</span>
                        </div>
                        <p className="text-sm text-slate-700 mb-2 leading-relaxed">{ex.good}</p>
                        <p className="text-xs text-emerald-600">{ex.why}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 动物故事专属 */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">🐾 动物故事专属技巧</h3>
                  <div className="space-y-2 mb-4">
                    {ANIMAL_TIPS.map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-2 bg-amber-50 rounded-lg p-3">
                        <span className="text-lg shrink-0">{tip.icon}</span>
                        <p className="text-sm text-slate-700">{tip.text}</p>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-3">
                    {ANIMAL_EXAMPLES.map((ex, idx) => (
                      <div key={idx} className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                        <div className="flex items-center gap-1.5 mb-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className="text-xs font-semibold text-emerald-700">{ex.title}</span>
                        </div>
                        <p className="text-sm text-slate-700 mb-2 leading-relaxed">{ex.good}</p>
                        <p className="text-xs text-emerald-600">{ex.why}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 注意事项 */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">⚠️ 避免这样写</h3>
                  <div className="space-y-2">
                    {WARNINGS.map((w, idx) => (
                      <div key={idx} className="flex items-start gap-2 bg-rose-50 rounded-lg p-3">
                        <AlertCircle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />
                        <p className="text-sm text-slate-700">{w}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
